import {
  productFamilyIdFormat,
  productFamilyImageIdFormat,
  productFamilyOptionIdFormat,
  productFamilyOptionValueIdFormat,
  productIdFormat,
  productImageIdFormat,
} from "@redotech/redo-model/ids";
import { AttributeSchema } from "@redotech/redo-model/inventory/attributes";
import { ProductFamilyKindSchema } from "@redotech/redo-model/inventory/general-listing/general-listing";
import { ListingsWithStatusSchema } from "@redotech/redo-model/inventory/listing-status";
import * as z from "zod/v4";

const ImageSchema = z.object({
  id: z.string(),
  url: z.string(),
  altText: z.string(),
  thumbhash: z.string().nullish(),
  position: z.number(),
});

const ProductPriceSchema = z.object({
  productId: productIdFormat.schema,
  price: z.number(),
  currency: z.string(),
});

const ProductOptionValueInputSchema_ = z.object({
  id: productFamilyOptionValueIdFormat.schema.optional(),
  value: z.string().min(1),
});
export const ProductOptionValueInputSchema: z.ZodType<ProductOptionValueInput> =
  ProductOptionValueInputSchema_;
export interface ProductOptionValueInput
  extends z.infer<typeof ProductOptionValueInputSchema_> {}

const ProductOptionInputSchema_ = z.object({
  id: productFamilyOptionIdFormat.schema.optional(),
  name: z.string().min(1),
  values: z.array(ProductOptionValueInputSchema_),
});
export const ProductOptionInputSchema: z.ZodType<ProductOptionInput> =
  ProductOptionInputSchema_;
export interface ProductOptionInput
  extends z.infer<typeof ProductOptionInputSchema_> {}

// Product option selection for creating/updating products
const ProductOptionSelectionInputSchema_ = z.object({
  optionId: productFamilyOptionIdFormat.schema.optional(),
  valueId: productFamilyOptionValueIdFormat.schema.optional(),
  optionName: z.string(),
  valueName: z.string(),
});
export const ProductOptionSelectionInputSchema: z.ZodType<ProductOptionSelectionInput> =
  ProductOptionSelectionInputSchema_;
export interface ProductOptionSelectionInput
  extends z.infer<typeof ProductOptionSelectionInputSchema_> {}

// Product input for creating/updating products
const ProductInputSchema_ = z.object({
  id: productIdFormat.schema.optional(),
  title: z.string(),
  sku: z.string().optional(),
  price: z.number().optional(),
  currency: z.string(),
  optionValues: z.array(ProductOptionSelectionInputSchema_),
});
export const ProductInputSchema: z.ZodType<ProductInput> = ProductInputSchema_;
export interface ProductInput extends z.infer<typeof ProductInputSchema_> {}

// Listing image selection for Shopify listings
const ListingImageSelectionSchema_ = z.object({
  imageId: z.union([
    productFamilyImageIdFormat.schema,
    productImageIdFormat.schema,
  ]),
  source: z.enum(["product_family", "product"]),
  position: z.number(),
});
export const ListingImageSelectionSchema: z.ZodType<ListingImageSelection> =
  ListingImageSelectionSchema_;
export interface ListingImageSelection
  extends z.infer<typeof ListingImageSelectionSchema_> {}

const ListingImagesInputSchema_ = z.object({
  selectedImages: z.array(ListingImageSelectionSchema_),
  isUsingDefaults: z.boolean(),
});
export const ListingImagesInputSchema: z.ZodType<ListingImagesInput> =
  ListingImagesInputSchema_;
export interface ListingImagesInput
  extends z.infer<typeof ListingImagesInputSchema_> {}

export const updateProductFamily = {
  name: "updateProductFamily" as const,
  input: z.object({
    productFamilyId: productFamilyIdFormat.schema,
    title: z.string().optional(),
    description: z.string().optional(),
    vendor: z.string().nullable().optional(),
    tags: z.array(z.string()).optional(),
    kind: ProductFamilyKindSchema.optional(),
    attributes: z.array(AttributeSchema).optional(),
    images: z.array(ImageSchema).optional(),
    productPrices: z.array(ProductPriceSchema).optional(),
    productOptions: z.array(ProductOptionInputSchema_).optional(),
    listings: ListingsWithStatusSchema,
    products: z.array(ProductInputSchema_).optional(),
    productsToDelete: z.array(productIdFormat.schema).optional(),
    listingImages: ListingImagesInputSchema_.optional(),
  }),
  output: z.object({ success: z.boolean() }),
};
